import React, { useCallback, useMemo, useState } from "react";
// import PersonAddOutlinedIcon from "@mui/icons-material/PersonAddOutlined";
import { toast } from "react-toastify";

import AddchartIcon from "@mui/icons-material/Addchart";
import AutoGraphIcon from "@mui/icons-material/AutoGraph";
import DeleteIcon from "@mui/icons-material/Delete";
import InsertChartOutlinedIcon from "@mui/icons-material/InsertChartOutlined";
import EmailIcon from "@mui/icons-material/MailOutline";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import QuickreplyOutlinedIcon from "@mui/icons-material/QuickreplyOutlined";
import { Card, CardContent, CardMedia, IconButton, Stack, Typography } from "@mui/material";
import classNames from "classnames";
import { RoleComponentGuard } from "components";
import { NavigateLink } from "components/Navigate-link/NavigateLink";
import { Menu } from "components/popups";
import { MaterialTooltip } from "components/tooltips/MaterialTooltip";
import { useNavigate } from "hooks";
import {
  useAllowDealTabOpenSelector,
  useDealActions,
  usePickUserPermissions,
  useUserRoleSelector,
  useViewActions
} from "store";
import { CurrencyType, ICapitalSeeker, IDealPreview, IRole, UserPermissionType } from "store/types";
import { copyToClipboard } from "utils";
import { isRoleAnalyst } from "utils/user";

import { RoleName } from "../../../constants";

export interface Props extends IDealPreview {
  onClickDelete?: (id: number, title: string) => void;
}

export const GridCell: React.FC<Props> = ({
  onClickDelete,
  title,
  status,
  id,
  deal_capital_seeker,
  currency: currencyCharacteristic,
  asset_class,
  email,
  deal_owner_value,
  logo
}) => {
  const navigate = useNavigate();
  const { setCharacteristicsManageAction, setReachOutManageAction } = useDealActions();
  const { setCreateManyMeasureData } = useViewActions();
  const role: IRole | null = useUserRoleSelector();
  const portfolioAnalysis = usePickUserPermissions("portfolio_analysis");
  const myDeals = usePickUserPermissions("deals");
  const dealMetrics = usePickUserPermissions("deal_metrics");
  const noAccessPermissionReport = portfolioAnalysis && portfolioAnalysis === UserPermissionType.NO_ACCESS;
  const noAccessPermissionMetrics = dealMetrics && dealMetrics === UserPermissionType.NO_ACCESS;
  const isViewerPermissionDeals = myDeals && myDeals === UserPermissionType.VIEWER;

  const capitalSeeker: ICapitalSeeker | undefined = deal_capital_seeker?.[0];
  const currency = currencyCharacteristic?.data?.find((curr) => curr.is_default)?.value as CurrencyType;

  const dealInfo: string = title + ` ${"<" + email + ">"}`;

  const onClickCopy = useCallback(() => {
    try {
      copyToClipboard(dealInfo);
      toast("Email address was copied to clipboard");
    } catch (_error) {
      console.log("Something went wrong, impossible to copy deal email");
    }
  }, [dealInfo]);

  const goToReport = useCallback(() => {
    if (id) {
      navigate({ route: "dealReport", params: { deal_id: id } });
    }
  }, [id]);

  const csMail = capitalSeeker?.email || "";

  const meniItems = useMemo(() => {
    const items = [
      {
        title: "Characteristics",
        icon: <MoreHorizIcon />,
        onClick: () =>
          setCharacteristicsManageAction({
            dealName: title,
            dealId: id,
            viewOnly: isViewerPermissionDeals || role?.name === RoleName.TeamViewer
          }),
        hidden: !isRoleAnalyst({ role })
      },
      {
        title: "Add Value",
        icon: <AddchartIcon />,
        onClick: () => setCreateManyMeasureData({ dealId: id, dealTitle: title }),
        hidden: !isRoleAnalyst({
          role,
          preventTV: role?.name === RoleName.TeamViewer,
          preventUser: noAccessPermissionMetrics
        })
      },
      // {
      //   title: "Share deal",
      //   icon: <PersonAddOutlinedIcon />,
      //   onClick: () =>
      //     setShareDialog({
      //       elements: [id],
      //       type: "deal_id",
      //       shareName: title,
      //       dealId: id,
      //       dealCapitalSeekerEmail: csMail
      //     }),
      //   hidden: !isRoleAnalyst(role)
      // },
      {
        title: "Email",
        icon: <EmailIcon />,
        onClick: () => onClickCopy()
      },
      {
        title: "Cashflow and Data",
        icon: <InsertChartOutlinedIcon />,
        onClick: () => {
          navigate({ route: "dealMonitoringData", params: { deal_id: id } });
        },
        hidden: !isRoleAnalyst({ role, preventUser: noAccessPermissionMetrics })
      }
    ];
    if (onClickDelete) {
      items.push({
        title: "Delete Deal",
        icon: <DeleteIcon />,
        onClick: () => onClickDelete(id, title)
      });
    }
    return items;
  }, [id, title, onClickDelete, currency, id, title, csMail]);

  const [isOpenAvailable, setIsOpenAvailable] = useState<boolean>(true);
  const allowNewTab = useAllowDealTabOpenSelector();
  return (
    <NavigateLink allowNewTab={allowNewTab} route="deal" params={{ deal_id: String(id) }}>
      <Card
        className={classNames("deal-card", { "deal-card--clickable": isOpenAvailable })}
        sx={{
          boxShadow: "0 0 15px 0 rgba(0, 0, 0, 0.07)",
          padding: "10px 10px 0px 10px",
          borderRadius: "20px",
          minHeight: "190px",
          width: "381px",
          minWidth: "381px",
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-evenly",
          alignItems: "stretch",
          color: "#0a3142"
        }}
      >
        <CardContent
          className="deal-card__content"
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            minHeight: "130px",
            cursor: "pointer",
            borderRadius: "20px",
            border: "solid 1px #eaedef",
            padding: "25px",
            gap: "20px"
          }}
        >
          <Stack direction="row" alignItems="center" gap="15px">
            {logo && (
              <CardMedia
                component="img"
                src={logo.file_url}
                sx={{ objectFit: "contain", width: "50px", height: "50px" }}
              />
            )}
            <Stack alignItems="stretch" gap="8px" overflow="hidden">
              <MaterialTooltip title={title} placement="top">
                <Typography
                  sx={{
                    fontSize: "20px",
                    fontWeight: "bold",
                    whiteSpace: "nowrap",
                    textOverflow: "ellipsis",
                    overflow: "hidden",
                    maxWidth: "-webkit-fill-available",
                    lineHeight: 1
                  }}
                >
                  {title}
                </Typography>
              </MaterialTooltip>
              <Typography lineHeight={1} fontSize="16px">
                Asset Class: {asset_class?.account_alias || "-"}
              </Typography>
            </Stack>
          </Stack>
          <Typography fontSize="15px">Owner: {deal_owner_value}</Typography>
        </CardContent>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Stack
            alignItems="center"
            className="deal-card__area-actions"
            sx={{
              bgcolor: statusColors[status.id] ? statusColors[status.id].bgc : statusColors["1"].bgc,
              color: statusColors[status.id] ? statusColors[status.id].color : statusColors["1"].color,
              padding: "5px 0px",
              borderRadius: "20px",
              minWidth: "116px"
            }}
          >
            {status.name}
          </Stack>
          <Stack direction="row" gap="5px">
            <RoleComponentGuard
              availableRoles={[RoleName.Admin, RoleName.Analyst]}
              hideChild={noAccessPermissionReport}
            >
              {(status?.name === "Closed" || status?.name === "Invested") && (
                <MaterialTooltip isShow title="Deal Report">
                  <IconButton
                    onMouseEnter={() => setIsOpenAvailable(false)}
                    onMouseLeave={() => setIsOpenAvailable(true)}
                    sx={{ padding: "6px" }}
                    onClick={(event) => {
                      event.stopPropagation();
                      event.preventDefault();
                      goToReport();
                    }}
                  >
                    <AutoGraphIcon sx={{ color: "#5d7782" }} />
                  </IconButton>
                </MaterialTooltip>
              )}
            </RoleComponentGuard>
            <RoleComponentGuard availableRoles={[RoleName.Admin, RoleName.Analyst]} hideChild={isViewerPermissionDeals}>
              <MaterialTooltip isShow title="Reach Out">
                <IconButton
                  onMouseEnter={() => setIsOpenAvailable(false)}
                  onMouseLeave={() => setIsOpenAvailable(true)}
                  sx={{ padding: "6px" }}
                  onClick={(event) => {
                    event.stopPropagation();
                    event.preventDefault();
                    setReachOutManageAction({
                      dealId: id,
                      dealName: title,
                      email: capitalSeeker?.email
                    });
                  }}
                >
                  <QuickreplyOutlinedIcon sx={{ color: "#5d7782" }} />
                </IconButton>
              </MaterialTooltip>
            </RoleComponentGuard>
            <div onMouseEnter={() => setIsOpenAvailable(false)} onMouseLeave={() => setIsOpenAvailable(true)}>
              <Menu keepTriggerEnabled iconButtonProps={{ size: "sm", sx: { color: "#5d7782" } }} options={meniItems} />
            </div>
          </Stack>
        </Stack>
      </Card>
    </NavigateLink>
  );
};

const statusColors: { [key: number]: { [key: string]: string } } = {
  "1": { bgc: "#edf5ff", color: "#224c7e" },
  "2": { bgc: "#fff7da", color: "#b17500" },
  "3": { bgc: "#f1f1f1", color: "#606060" },
  "4": { bgc: "#e6fce8", color: "#3d7943" },
  "5": { bgc: "#ffeeee", color: "#c03636" },
  "6": { bgc: "#f1f1f1", color: "#606060" }
};
